import { ClinicProduct, ListResult, PublicProductQuery } from '../types';
import { USE_MOCK_PUBLIC_CATALOG } from '../constants/buyer-portal';
import { publicCatalogMock } from '../mocks/publicCatalogMock';
import { clinicHttp } from './http';
import { ENDPOINTS } from './endpoints';
import { mapProductBuyerDto, ProductBuyerDtoBackend } from './productMapper';

export type PublicProductFacets = {
  brands: { key: string; count: number }[];
  priceMin: number;
  priceMax: number;
  items: ClinicProduct[];
};

export type PublicCatalogService = {
  list: (query?: PublicProductQuery) => Promise<ListResult<ClinicProduct>>;
  detail: (productId: string) => Promise<ClinicProduct>;
  facets: () => Promise<PublicProductFacets>;
};

type BackendListResult = {
  items: ProductBuyerDtoBackend[] | null;
  skip: number;
  take: number;
  total: number;
};

const computeFacets = (items: ClinicProduct[]): PublicProductFacets => {
  const brandCounts = new Map<string, number>();
  let min = Infinity;
  let max = 0;
  for (const p of items) {
    if (p.brand) brandCounts.set(p.brand, (brandCounts.get(p.brand) ?? 0) + 1);
    if (p.salePrice < min) min = p.salePrice;
    if (p.salePrice > max) max = p.salePrice;
  }
  return {
    brands: Array.from(brandCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count),
    priceMin: Math.floor(min === Infinity ? 0 : min),
    priceMax: Math.ceil(max || 0),
    items,
  };
};

const realService: PublicCatalogService = {
  list: async (query = {}) => {
    const res = await clinicHttp.get<BackendListResult>(ENDPOINTS.PUBLIC.PRODUCTS, {
      params: query,
    });
    return {
      items: (res.data.items ?? []).map(mapProductBuyerDto),
      skip: res.data.skip,
      take: res.data.take,
      total: res.data.total,
    };
  },
  detail: async (productId) => {
    const res = await clinicHttp.get<ProductBuyerDtoBackend>(
      ENDPOINTS.PUBLIC.PRODUCT_DETAIL(productId),
    );
    return mapProductBuyerDto(res.data);
  },
  facets: async () => {
    const res = await clinicHttp.get<BackendListResult>(ENDPOINTS.PUBLIC.PRODUCTS, {
      params: { take: 200 },
    });
    return computeFacets((res.data.items ?? []).map(mapProductBuyerDto));
  },
};

export const publicCatalogService: PublicCatalogService = USE_MOCK_PUBLIC_CATALOG
  ? publicCatalogMock
  : realService;
